import { Inject } from '@odinjs/odin';
import { ActionAPI, BaseModalProps, ButtonAPI, DeckAPI, ModalButton, ModalService, PanelAPI } from '@philips-internal/oswald-ui-components';
import { ModalButtonType, ModalType, ToastLevel } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { AbortableCloseEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { BloodManagementViewerAliases as BloodAliases, FragmentNames } from '../../../../../codegen';
import { Endpoint } from '../../../../../constants/endpoints';
import { ActionType, InfusionHourStatus, InfusionItemStatus } from '../../../../../constants/infusion-solutions';
import { ApplicationRoute } from '../../../../../constants/routes';
import { YesNoType } from '../../../../../utils/enum-utils';
import { FoundationUtils } from '../../../../../utils/foundation-utils';
import { ADEP_E1113Parameters, FunctionName } from '../../../../../utils/parameters';
import ShowToast from '../../../../../utils/toast-service';
import BaseController from '../../../../BaseController';
import InformationBarUtils from '../../../../commons/header/information-bar-header-utils';
import { MedicationAndDateHeaderParams } from '../../../../commons/header/medication-and-date-header-params';
import TimelineContext, { PrescriptionType, TimeLineEventMedication } from '../../../services/timeline-service';
import { AdminByScheduleParams } from '../../administration-schedule/administration-schedule-params';
import { AdminByProcessParams } from '../../medication-administration-by-process/medication-administration-by-process-params';
import { MedicationViewerParams } from '../../medication-viewer/medication-viewer-params';
import { BloodManagementType, DoubleCheckBloodManagement } from '../blood-management-types';
import { MedicineAndSolutionsParams } from '../../medicine-and-solutions/medicine-and-solutions-params';

@Controller({ component: BloodAliases.BLOOD_MANAGEMENT_VIEWER_LAYOUT, fragment: FragmentNames.BLOOD_MANAGEMENT_VIEWER })
export default class BloodManagementViewerController extends BaseController {

  @Inject(BloodAliases.BLOOD_MANAGEMENT_CONTENT_PANEL)
  private contentPanelAPI!: PanelAPI;

  @Inject(BloodAliases.START_BUTTON)
  private startButtonAPI!: ButtonAPI;

  @Inject(BloodAliases.INTERRUPT_BUTTON)
  private interruptButtonAPI!: ButtonAPI;

  @Inject(BloodAliases.BLOOD_MANAGEMENT_FOOTER_PANEL)
  private footerPanelAPI!: PanelAPI;

  @OnMount(BloodAliases.BLOOD_MANAGEMENT_VIEWER_LAYOUT)
  handleMountLayout(): void {
    InformationBarUtils.collapsedBar = true;
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    const timelineMedication = TimelineContext.get() as TimeLineEventMedication;
    this.fetchBloodManagement().then((response) => {
      const bloodManagement = response as BloodManagementType;
      MedicationViewerParams.adminSchedule = {
        dilutionDescription: '',
        materialSequence: timelineMedication.cpoeSequence,
        medicationHour: timelineMedication.start,
        prescriptionNumber: timelineMedication.prescriptionNumber,
      };
      MedicineAndSolutionsParams.params = {
        encounterId: encounterId as string,
        hourSequence: String(timelineMedication.id),
        ieTypeItem: timelineMedication.itemType,
        nrPrescriptionId: String(timelineMedication.prescriptionNumber),
        nrSequenceId: String(timelineMedication.itemSequence),
        totalVolume: bloodManagement.totalVolume,
      };
      MedicationViewerParams.params = {
        itemCode: String(timelineMedication.itemCode)
      };
      MedicationAndDateHeaderParams.medicationAndDateHeaderProps = {
        medicationName: timelineMedication.description,
        scheduleDate: timelineMedication.start,
        type: 'BLOOD_MANAGEMENT'
      };

      this.contentPanelAPI.setVisible(true);
      this.footerPanelAPI.setVisible(true);
      this.context.set({
        bloodManagement: {
          ...bloodManagement,
          itemStatus: timelineMedication.status,
          materialDescription: timelineMedication.description,
          materialSequence: timelineMedication.itemSequence,
          prescriptionNumber: timelineMedication.prescriptionNumber,
          scheduleDate: timelineMedication.start,
          scheduleSequence: timelineMedication.itemSequence,
          status: timelineMedication.status,
          stepNumber: timelineMedication.emarStepNumber
        },
        encounterId,
        patientId,
      })
    }).catch((error) => {
      if (error.body) {
        ShowToast.open(error.body.errorMessage, ToastLevel.ERROR)
      }
    })
  }

  @OnMount(BloodAliases.BLOOD_MANAGEMENT_DECK)
  handleBloodManagementMount({ target }: EventData<DeckAPI>): void {
    const timelineMedication = TimelineContext.get() as TimeLineEventMedication;
    const bloodManagement = this.context.get('bloodManagement') as BloodManagementType;
    target.setData([{
      currentSpeedInfusion: bloodManagement.infusionSpeed,
      description: timelineMedication.description,
      doseQuantity: timelineMedication.itemQuantity,
      endDate: bloodManagement.endDate,
      isChemotherapy: false,
      prescriptionId: timelineMedication.prescriptionNumber,
      prescriptionNumber: timelineMedication.prescriptionNumber,
      scheduleDate: timelineMedication.start,
      statusItem: timelineMedication.status,
      totalVolume: bloodManagement.totalVolume,
      unitMeasurement: bloodManagement.unitMeasurement
    }]);
  }

  @OnClick(BloodAliases.START_BUTTON)
  handleClickExecuteTimeButton(): void {
    const scheduleTitleDescription = MessageService.translate('text.emar-administration');
    const bloodManagement = this.context.getBulkData() as Record<string, Value>;
    const dialogModal: BaseModalProps = {
      contextData: bloodManagement,
      focusOnOpen: false,
      fragment: FragmentNames.BLOOD_MANAGEMENT_START,
      id: FragmentNames.BLOOD_MANAGEMENT_START,
      onBeforeClose: (event) => this.dialogModalConfirm(event, FragmentNames.BLOOD_MANAGEMENT_START),
      title: scheduleTitleDescription,
      type: ModalType.DEFAULT,
    };

    ModalService.openDefault(dialogModal);
  }

  @OnMount(BloodAliases.START_BUTTON)
  handleStartButtonMount(): void {
    this.validateAndSetButtonVisibility();
  }

  @OnMount(BloodAliases.INTERRUPT_BUTTON)
  handleMountInterruptButton(): void {
    const { itemStatus } = this.historyService.getCurrentRouteParams() as { itemStatus: string };
    const isValidItemStatusForInterrupt = [InfusionItemStatus.STARTED, InfusionItemStatus.RESTARTED].some(status => status === itemStatus);
    this.interruptButtonAPI.setVisible(isValidItemStatusForInterrupt);
  }

  @OnClick(BloodAliases.INTERRUPT_BUTTON)
  handleClickInterruptButton(): void {
    const interruptExpression = 'button.infusion-viewer.interrupt';
    const interruptDescription = MessageService.translate(interruptExpression);
    const bloodManagement = this.context.getBulkData() as unknown as BloodManagementType;
    const dialogModal: BaseModalProps = {
      contextData: { bloodManagement },
      focusOnOpen: false,
      fragment: FragmentNames.BLOOD_MANAGEMENT_INTERRUPT,
      id: FragmentNames.BLOOD_MANAGEMENT_INTERRUPT,
      onBeforeClose: (event) => this.dialogModalConfirm(event, FragmentNames.BLOOD_MANAGEMENT_INTERRUPT),
      title: interruptDescription,
      type: ModalType.DEFAULT,
    };
    AdminByProcessParams.admType = { isAdmProcess: false };
    ModalService.openDefault(dialogModal);
  }

  @OnClick(BloodAliases.CONTEXTUAL_BAR_ACTION_TERMINATE)
  handleClickTerminateAction(): void {
    const timelineMedication = TimelineContext.get() as TimeLineEventMedication;
    const bloodManagement = this.context.get('bloodManagement') as BloodManagementType;

    const { patientId, encounterId, scheduleSequenceNumber, startDate } = this.historyService.getCurrentRouteParams();
    applicationContext.redirect(ApplicationRoute.ADEP_BLOOD_TERMINATE, {
      encounterId,
      partialVolume: String(bloodManagement.partialVolume ?? 0),
      patientId,
      prescriptionNumber: timelineMedication.prescriptionNumber,
      scheduleSequenceNumber,
      sequenceNumber: timelineMedication.itemSequence,
      startDate,
      stepVolume: bloodManagement.stepVolume,
    });
  }

  @OnMount(BloodAliases.CONTEXTUAL_BAR_ACTION_TERMINATE)
  handleMountActionTerminate({ target }: EventData<ActionAPI>): void {
    const { itemStatus } = this.historyService.getCurrentRouteParams();
    const isValidStatus = [InfusionItemStatus.STARTED, InfusionItemStatus.RESTARTED, InfusionItemStatus.INTERRUPTED, InfusionItemStatus.II].some(st => st === itemStatus);
    target.setEnabled(isValidStatus);
  }

  @OnMount(BloodAliases.CONTEXTUAL_BAR_ACTION_SUSPEND)
  handleSuspendBloodTransfusionActionMount(event: MountEvent): void {
    const actionApi = event.target as ActionAPI;
    actionApi.setEnabled(this.isPending());
  }

  @OnClick(BloodAliases.CONTEXTUAL_BAR_ACTION_SUSPEND)
  handleSuspendBloodTransfusionActionClick(): void {
    const { patientId, encounterId } = this.context.getBulkData() as Record<string, string>;
    applicationContext.redirect(ApplicationRoute.ADEP_SUSPEND_ITEM, {
      encounterId,
      patientId,
    });
  }

  @OnMount(BloodAliases.CONTEXTUAL_BAR_ACTION_REVERSE)
  handleMountActionReverse(event: MountEvent): void {
    const actionApi = event.target as ActionAPI
    actionApi.setEnabled(this.isAllowedToReverse());
  }

  @OnClick(BloodAliases.CONTEXTUAL_BAR_ACTION_REVERSE)
  handleClickActionReverse(): void {
    const { itemSequence, prescriptionNumber } = TimelineContext.get() as TimeLineEventMedication;
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    const itemType = PrescriptionType.BLOOD_MANAGEMENT;
    applicationContext.redirect(ApplicationRoute.ADEP_BLOOD_REVERSE, { encounterId, itemType, patientId, prescriptionNumber, sequenceNumber: itemSequence });
  }

  @OnMount(BloodAliases.CONTEXTUAL_BAR_ACTION_RECORD_REACTION)
  handleActionRecordReactionMount(event: MountEvent): void {
    const api = event.target as ActionAPI;
    const { productReserved } = this.context.get('bloodManagement') as BloodManagementType;
    const { itemStatus } = this.historyService.getCurrentRouteParams();
    api.setEnabled(!!productReserved && this.isValidStatus(String(itemStatus)));
  }

  @OnClick(BloodAliases.CONTEXTUAL_BAR_ACTION_RECORD_REACTION)
  handleActionReactionClick(): void {
    const { prescriptionNumber } = TimelineContext.get() as TimeLineEventMedication;
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    const { productReserved } = this.context.get('bloodManagement');
    applicationContext.redirect(ApplicationRoute.ADEP_BLOOD_RECORD_TRANSFUSION_REACTION, { encounterId, patientId, prescriptionNumber, productReserved });
  }

  async validateAndSetButtonVisibility(): Promise<string | void> {
    const { hourStatus, itemStatus } = this.historyService.getCurrentRouteParams() as { hourStatus: string, itemStatus: string };
    const isValidItemStatusForAdministered = [InfusionItemStatus.II, InfusionItemStatus.INTERRUPTED, InfusionItemStatus.RESTARTED, InfusionItemStatus.T].some(status => status === itemStatus);
    const isNotStarted = hourStatus === InfusionHourStatus.PENDING && itemStatus === InfusionItemStatus.RESTARTED
    const isDoubleCheck = await this.isDoubleCheckBloodManagement();
    this.context.set({isDoubleCheck})

    if (this.hideStartButton(isValidItemStatusForAdministered, isNotStarted, hourStatus)) {
      this.startButtonAPI.setVisible(false);
      return;
    }

    if (this.shouldStartBlood(itemStatus, hourStatus)) {
      const label = this.setScreenLabel(ActionType.START_SOLUTION, hourStatus);
      this.setExecuteTimeButton(label)
      return;
    }

    if (this.shouldRestartBlood(itemStatus, hourStatus)) {
      const label = this.setScreenLabel(ActionType.RESTART_SOLUTION, hourStatus);
      this.setExecuteTimeButton(label)
      return;
    }

    this.startButtonAPI.setVisible(false);
  }

  private hideStartButton(isValidItemStatusForAdministered: boolean, isNotStarted: boolean, hourStatus: string): boolean {
    return (hourStatus === InfusionHourStatus.ADMINISTERED && isValidItemStatusForAdministered) || isNotStarted;
  }

  private shouldStartBlood(itemStatus: string, status: string): boolean {
    const isItemStatusValid = itemStatus === InfusionItemStatus.NOT_STARTED;
    const isStatusValid = status === InfusionHourStatus.PENDING ||
      status === InfusionHourStatus.PREPARATION ||
      status === InfusionHourStatus.CHECKED_FIRST;
    return isItemStatusValid && isStatusValid
  }

  private shouldRestartBlood(itemStatus: string, status: string): boolean {
    const isInterrupted = this.isInterrupted(itemStatus) && status === InfusionHourStatus.INTERRUPTED;
    const isPending = itemStatus === InfusionItemStatus.INTERRUPTED && status === InfusionHourStatus.PENDING;
    return isInterrupted || isPending;
  }

  private setExecuteTimeButton(label: string): void {
    this.startButtonAPI.setValue(MessageService.translate(label));
    this.startButtonAPI.setVisible(true);
  }

  private setScreenLabel(actionType: string, status: string): string {
    const { itemStatus } = this.historyService.getCurrentRouteParams() as { itemStatus: string };
    const isInterrupted = this.isInterrupted(itemStatus)
    const isDoubleCheck = this.context.getBulkData().isDoubleCheck;

    if (this.validateRestartButton(actionType, status, isInterrupted, itemStatus)) {
      return MessageService.translate('txt.restart-transfusion')
    }

    if (status !== InfusionHourStatus.STARTED) {
      return String(isDoubleCheck) === YesNoType.YES && status != InfusionHourStatus.CHECKED_FIRST ?
        MessageService.translate('tws.emar.perform-first-check') :
        MessageService.translate('txt.start-transfusion');
    }

    return ''
  }

  private isInterrupted(itemStatus: string): boolean {
    return [InfusionItemStatus.II.valueOf(), InfusionItemStatus.INTERRUPTED.valueOf()].includes(itemStatus)
  }

  private fetchBloodManagement(): Promise<BloodManagementType> {
    const { itemSequence, prescriptionNumber } = TimelineContext.get() as TimeLineEventMedication;

    return HttpService.get<BloodManagementType>(Endpoint.EMAR_GET_BLOOD_MANAGEMENT_INFORMATION, {
      itemSequence, prescriptionNumber
    }).then((response) => {
      const responseBody = response.body as BloodManagementType;
      return responseBody;
    })
  }

  private isDoubleCheckBloodManagement(): Promise<string | void> {
    const { itemSequence, itemType, prescriptionNumber } = TimelineContext.get() as TimeLineEventMedication;

    return HttpService.get(Endpoint.EMAR_GET_REALIZE_DOUBLE_CHECK, {
      itemType, prescriptionNumber, solutionSequenceNumber: itemSequence,
    }).then((response) => {
      const responseBody = response?.body as DoubleCheckBloodManagement;
      return responseBody.doubleCheck;
    })
  }

  private dialogModalConfirm(event: AbortableCloseEvent, id: string): void {
    const noCardsRead = AdminByScheduleParams.readParam?.noCardsRead;

    if (noCardsRead) {
      ModalService.close(id);
      ModalService.close('dialog-modal-service');
      return;
    }

    event.abort();

    const buttons: ModalButton[] = [];

    buttons.push({
      action: () => { },
      title: 'tws.biometry.cancel',
      type: ModalButtonType.CANCEL,
    });

    buttons.push({
      action: () => this.redirectToTimeline(id),
      title: 'tws.file-uploader-modal.confirm',
      type: ModalButtonType.SUCCESS,
    });

    const dialogModal: BaseModalProps = {
      dialogDescription: MessageService.translate('text.reset items-want-to-continue'),
      dockButtons: buttons,
      focusOnOpen: true,
      id: 'dialog-modal-service',
      title: 'text.message-alert-attention',
    };

    ModalService.openDialog(dialogModal);
  }

  private redirectToTimeline(id: string): void {
    ModalService.close(id);
    ModalService.close('dialog-modal-service');
  }

  private isPending(): boolean {
    const { hourStatus, itemStatus } = this.historyService.getCurrentRouteParams() as { hourStatus: string, itemStatus: string };
    return itemStatus === InfusionItemStatus.NOT_STARTED || hourStatus === InfusionHourStatus.PENDING;
  }

  private isAllowedToReverse(): boolean {
    const allowRevertParam = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_THE_LAST_EVENT_OF_INFUSIONS_TO_BE_REVERSED)
    if (allowRevertParam !== YesNoType.YES) {
      return false
    }
    const { itemStatus } = this.historyService.getCurrentRouteParams();
    return this.isValidStatus(String(itemStatus));
  }

  private isValidStatus(itemStatus: string): boolean {
    return [
      InfusionItemStatus.STARTED,
      InfusionItemStatus.RESTARTED,
      InfusionItemStatus.INTERRUPTED,
      InfusionItemStatus.II,
      InfusionItemStatus.T
    ].some(st => st === itemStatus);
  }

  validateRestartButton(actionType: string, status: string, isInterrupted: boolean, itemStatus: string): boolean {
    return actionType !== ActionType.START_SOLUTION ||
      (status === InfusionHourStatus.INTERRUPTED && isInterrupted) ||
      (status === InfusionHourStatus.PENDING && itemStatus === InfusionItemStatus.INTERRUPTED);
  }
}